import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Button, { ButtonDecor } from 'vibo-ui/Button';
import WithEventRole from 'components/user/WithEventRole';

import { useModal } from 'vibo-ui/Modal';
import { useEvent } from 'components/context/EventContext';
import { MAX_EVENT_TITLE_LENGTH } from 'services/constants';
import { openErrorNotification, toastNotify } from 'graphql/hooks/common';
import { appLightColor } from 'components/context/ViboThemeContext/constants';
import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';

import { DELETE_EVENT, RESTORE_EVENT, SAVE_EVENT_AS_TEMPLATE } from 'graphql/mutations/events';

import { IconmoonFont } from 'vibo-ui/Icon';
import { CloneModalProps } from 'components/modals/CloneModal';
import { DjNotesModalProps } from 'components/modals/DjNotesModal';
import { ButtonIconType, EventUserType, Modals } from 'types/enums';
import { ConfirmActionModalProps } from 'components/modals/ConfirmActionModal';
import { EventSettingsModalProps } from 'components/modals/EventSettingsModal';
import { PickBrandingColorModalProps } from 'components/modals/PickBrandingColorModal';

import useSettingsDropdownStyles from 'components/timeline/TimelineInfo/EventInfoCard/style';

const TimelineTools: FC = () => {
  const settingsDropdownClasses = useSettingsDropdownStyles();
  const { t } = useTranslation();

  const { id: eventId = '' } = useParams<keyof EventPageRouteParams>();
  const navigate = useNavigate();
  const eventContext = useEvent();
  const timeline = useTimeline<AllTimelineGqlOptions>();
  const { openModal } = useModal();

  const [saveAsTemplate, { loading: isCloning }] = useMutation(SAVE_EVENT_AS_TEMPLATE, {
    onError: () => openErrorNotification(t('somethingWentWrong')),
    onCompleted: () => {
      toastNotify({ text: t('copiedSuccessfully') });
    },
  });
  const [restoreEvent, { loading: isRestoring }] = useMutation(RESTORE_EVENT, {
    onError: () => openErrorNotification(t('somethingWentWrong')),
    onCompleted: () => {
      toastNotify({ text: t('eventHasRestored'), iconType: ButtonIconType.plus });
      navigate('/events/upcoming');
    },
    variables: {
      eventId,
    },
  });
  const [deleteEvent, { loading: isDeleting }] = useMutation(DELETE_EVENT, {
    onError: () => openErrorNotification(t('somethingWentWrong')),
    onCompleted: () => {
      toastNotify({ text: t('eventHasDeleted'), iconType: ButtonIconType.delete });
      navigate(`/events/history`);
    },
    variables: {
      eventId,
    },
  });
  const [updateEvent, { loading: isUpdating }] = useMutation(
    timeline.event.mutations.update.mutation,
    {
      onError: () => openErrorNotification(t('somethingWentWrong')),
      onCompleted: data => {
        toastNotify({ text: t('changesSavedSuccessfully') });

        if (data.updateEvent.isDeleted === true) {
          navigate(`/events${eventContext.isHistory ? '/history' : '/upcoming'}`);
        }
      },
    }
  );

  const openNotesModal = () =>
    openModal<DjNotesModalProps>({
      key: Modals.djNotes,
      props: {
        variables: { eventId },
        value: eventContext.event?.note ?? '',
        mutation: timeline.event.mutations.update.mutation,
      },
    });

  const openPickBrandingColorModal = () =>
    openModal<PickBrandingColorModalProps>({
      key: Modals.pickBrandingColor,
      props: {
        className: eventContext.isEventDj ? '' : 'hideProfileBtn',
        defaultColor: appLightColor(),
        title: t('eventColor'),
        handleUpdateColor,
      },
    });
  const openEventSettingsModal = () =>
    openModal<EventSettingsModalProps>({
      key: Modals.eventSettings,
      props: {
        eventContext,
        variables: { eventId },
        mutation: timeline.event.mutations.update.mutation,
      },
    });
  const openCloneTemplateModal = () =>
    openModal<CloneModalProps>({
      key: Modals.clone,
      props: {
        values: {
          title: eventContext.event?.title as string,
        },
        onSubmit: payload =>
          saveAsTemplate?.({
            variables: {
              eventId,
              payload,
            },
          }),
        maxLength: MAX_EVENT_TITLE_LENGTH,
        title: t('saveEventTemplate'),
        label: t('templateName'),
        closeOnSubmit: true,
      },
    });
  const openConfirmDeleteEventModal = () =>
    openModal<ConfirmActionModalProps>({
      key: Modals.confirmAction,
      props: {
        title: t('deleteEvent'),
        children: t('sureYouWantDeleteEvent', {
          event: eventContext.event?.title,
        }),
        submit: {
          text: t('delete'),
          onClick: handleDeleteEvent,
        },
      },
    });

  const goNotificationsPage = () => navigate(`/event/${eventId}/notifications`);

  const handleUpdateColor = (newColor: string) => {
    updateEvent({
      variables: {
        eventId,
        payload: {
          eventColor: newColor,
        },
      },
    });
  };

  const handleDeleteEvent = () =>
    eventContext.isHistory
      ? deleteEvent()
      : updateEvent({
          variables: {
            eventId,
            payload: {
              isDeleted: true,
            },
          },
        });

  return (
    <div className={settingsDropdownClasses.timelineTools}>
      <WithEventRole roles={[EventUserType.dj]}>
        <>
          {eventContext.isHistory ? (
            <div className="extraTools">
              <Button
                onClick={openCloneTemplateModal}
                prefixIcon={IconmoonFont['clone-16']}
                disabled={isCloning}
                displayType="bordered"
                shape="round"
                size="lg"
              >
                {t('saveAsTemplate')}
              </Button>
              <Button
                onClick={() => restoreEvent()}
                disabled={isRestoring}
                prefixIcon={IconmoonFont['refresh-16']}
                displayType="bordered"
                shape="round"
                size="lg"
              >
                {t('restoreEvent')}
              </Button>
              <Button
                onClick={openConfirmDeleteEventModal}
                className={ButtonDecor.danger}
                prefixIcon={IconmoonFont['delete-16']}
                disabled={isDeleting}
                displayType="bordered"
                shape="round"
                size="lg"
              >
                {t('deleteForever')}
              </Button>
            </div>
          ) : null}
          <Button
            onClick={openNotesModal}
            tooltipProps={{
              overlay: t('myNotes'),
              fillOverlay: true,
              placement: 'top',
            }}
            prefixIcon={IconmoonFont[!!eventContext.event?.note ? 'notes-24' : 'emptyNotes-24']}
            displayType="white"
            shape="round"
            size="xl"
          />
          <Button
            onClick={goNotificationsPage}
            tooltipProps={{
              overlay: 'Edit push notifications',
              fillOverlay: true,
              placement: 'top',
            }}
            prefixIcon={IconmoonFont['editPush-24']}
            displayType="white"
            shape="round"
            size="xl"
          />
          {eventContext.isHistory ? null : (
            <Button
              onClick={openEventSettingsModal}
              prefixIcon={IconmoonFont['editPencil-24']}
              tooltipProps={{
                overlay: 'Edit event settings',
                fillOverlay: true,
                placement: 'top',
              }}
              displayType="white"
              shape="round"
              size="xl"
            />
          )}
        </>
      </WithEventRole>
      <WithEventRole
        roles={[EventUserType.host]}
        extraCondition={eventContext.event?.settings?.canHostChangeEventColor}
      >
        <Button
          onClick={openPickBrandingColorModal}
          disabled={eventContext.event?.isLocked || isUpdating}
          tooltipProps={{
            overlay: t('eventColor'),
            fillOverlay: true,
            placement: 'top',
          }}
          prefixIcon={IconmoonFont['palette-24']}
          displayType="white"
          shape="round"
          size="xl"
        />
      </WithEventRole>
    </div>
  );
};

export default TimelineTools;
